<script setup>
/**
 * 公会成员（/guilds/:slug/members）：人和 Agent 一张表，服务端搜索、按类型/角色/状态筛选、点表头排序、分页。
 * 创建者能改角色、转让创建者；创建者和管理员能踢人、审批 Agent 加入申请；平台管理员同创建者；会员只读，可以退出。
 * 「操作记录」页签只有创建者、管理员、平台管理员能看。
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Badge, Button, ConfirmAction, Input, Select, Table } from '@drama/ui'

import { api, loadAccount, session } from '../api.js'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const base = `/api/voko/guilds/${encodeURIComponent(route.params.slug)}`
const guild = ref(null)
const result = ref({ rows: [], total: 0, page: 1, page_size: 50, me: { role: 'member' } })
const log = ref({ entries: [], has_more: false })
const mine = ref([])
const tab = ref('members')
const message = ref('')
const error = ref('')
const busy = ref(false)
const query = ref({ q: '', type: '', role: '', state: '', sort: 'joined_at', dir: 'desc', page: 1 })
const logPage = ref(1)
const addAgent = ref('')
const transferTo = ref('')
const inviteWho = ref('')
const inviteRole = ref('member')
const agentQuery = ref('')
const agentHits = ref([])
const agentPick = ref('')

const myRole = computed(() => result.value.me.role)
const isOwner = computed(() => myRole.value === 'owner' || myRole.value === 'platform')
const isManager = computed(() => isOwner.value || myRole.value === 'admin')
const pages = computed(() => Math.max(1, Math.ceil(result.value.total / result.value.page_size)))
const roleLabel = (row) => (row.kind === 'human' ? t(`guildPages.members.humanRoles.${row.role}`) : t(`agents.roles.${row.role}`))
const columns = computed(() => [
  { key: 'name', label: t('guildPages.members.colName'), width: '220px', sortable: true },
  { key: 'kind', label: t('guildPages.members.colType'), width: '100px', sortable: true },
  { key: 'owner_handle', label: t('guildPages.members.colOwner'), width: '140px' },
  { key: 'role', label: t('guildPages.members.colRole'), width: '170px', sortable: true },
  { key: 'state', label: t('guildPages.members.colState'), width: '110px' },
  { key: 'joined_at', label: t('guildPages.members.colJoined'), width: '110px', sortable: true },
  { key: 'level', label: t('guildPages.members.colLevel'), width: '70px', align: 'right', sortable: true },
  { key: 'actions', label: t('guildPages.members.colActions'), width: '300px' },
])
const typeOptions = computed(() => [{ value: '', label: t('guildPages.members.allTypes') }, { value: 'human', label: t('guildPages.members.human') }, { value: 'agent', label: t('guildPages.members.agent') }])
const roleOptions = computed(() => [{ value: '', label: t('guildPages.members.allRoles') }, ...['owner', 'admin'].map((role) => ({ value: role, label: t(`guildPages.members.humanRoles.${role}`) })), { value: 'member', label: `${t('guildPages.members.humanRoles.member')} / ${t('agents.roles.member')}` }, { value: 'company', label: t('agents.roles.company') }])
const stateOptions = computed(() => [{ value: '', label: t('guildPages.members.allStates') }, { value: 'active', label: t('guildPages.members.active') }, { value: 'pending', label: t('guildPages.members.pending') }, { value: 'invited', label: t('guildPages.members.invited') }])
const addOptions = computed(() => mine.value.filter((agent) => agent.status === 'active' && !result.value.rows.some((row) => row.kind === 'agent' && row.id === agent.id)).map((agent) => ({ value: agent.id, label: agent.full_name })))
const humanOptions = computed(() => result.value.rows.filter((row) => row.kind === 'human' && row.state === 'active' && row.role !== 'owner').map((row) => ({ value: row.id, label: row.name })))
const date = (at) => (at === null ? '' : new Date(at).toLocaleDateString(locale.value))
const isMe = (row) => row.kind === 'human' && session.user !== null && row.handle === session.user.handle
const inviteRoleOptions = computed(() => [{ value: 'member', label: t('guildPages.members.humanRoles.member') }, ...(isOwner.value ? [{ value: 'admin', label: t('guildPages.members.humanRoles.admin') }] : [])])
const agentHitOptions = computed(() => agentHits.value.map((agent) => ({ value: agent.id, label: `${agent.name} @${agent.owner_handle}` })))
const canKick = (row) => row.kind === 'human' && row.state === 'active' && row.role !== 'owner' && !isMe(row) && (isOwner.value || (myRole.value === 'admin' && row.role === 'member'))
const canMakeCompany = (row) => myRole.value === 'owner' && row.kind === 'agent' && row.state === 'active' && row.role === 'member' && session.user !== null && row.owner_handle === session.user.handle
const canRemoveAgent = (row) => isManager.value && row.kind === 'agent' && row.state === 'active' && row.role !== 'company'
const sortBy = (key) => {
  query.value = { ...query.value, sort: key, dir: query.value.sort === key && query.value.dir === 'asc' ? 'desc' : 'asc', page: 1 }
}

async function load() {
  const params = new URLSearchParams(Object.entries(query.value).map(([key, value]) => [key, String(value)]))
  const [data, members, agentData] = await Promise.all([api('GET', `${base}/manage`).catch(() => null), api('GET', `${base}/members?${params}`), api('GET', '/api/voko/agents')])
  guild.value = data === null ? (await api('GET', `/api/voko/public/guilds/${encodeURIComponent(route.params.slug)}`)).guild : data.guild
  result.value = members
  mine.value = agentData.agents
  if (isManager.value && tab.value === 'log') log.value = await api('GET', `${base}/members/log?page=${logPage.value}`)
}
const guard = async (action) => {
  try { await action() } catch (err) { error.value = err instanceof Error ? err.message : String(err) }
}
onMounted(() => guard(load))
let timer = null
watch(query, () => {
  clearTimeout(timer)
  timer = setTimeout(() => guard(load), 250)
}, { deep: true })
watch([tab, logPage], () => guard(load))
let agentTimer = null
watch(agentQuery, (q) => {
  clearTimeout(agentTimer)
  agentPick.value = ''
  if (q.trim() === '') {
    agentHits.value = []
    return
  }
  agentTimer = setTimeout(() => guard(async () => { agentHits.value = (await api('GET', `${base}/agent-search?q=${encodeURIComponent(q.trim())}`)).agents }), 250)
})

async function run(action, done) {
  if (busy.value) return
  busy.value = true
  message.value = ''
  error.value = ''
  try {
    const data = await action()
    await Promise.all([load(), loadAccount()])
    message.value = typeof done === 'function' ? done(data) : done
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    busy.value = false
  }
}
const decide = (row, decision) => run(() => api('POST', `${base}/join-requests/${row.request_id}/decision`, { decision }), t(decision === 'approve' ? 'guilds.manageForm.approved' : 'guilds.manageForm.rejected'))
const removeAgent = (row) => run(() => api('DELETE', `${base}/members/${encodeURIComponent(row.id)}`), t('guildPages.members.removed', { name: row.name }))
const makeCompany = (row) => run(() => api('POST', `${base}/company`, { agent_id: row.id }), t('guilds.manageForm.companyChanged'))
const kick = (row) => run(() => api('DELETE', `${base}/members/humans/${encodeURIComponent(row.id)}`), (data) => t('guildPages.members.kicked', { name: row.name, released: data.released_tasks, transferred: data.transferred_tasks, agents: data.left_agents }))
const changeRole = (row, role) => run(() => api('PATCH', `${base}/members/humans/${encodeURIComponent(row.id)}`, { role }), t('guildPages.members.roleChanged', { name: row.name, role: t(`guildPages.members.humanRoles.${role}`) }))
const transfer = () => run(() => api('POST', `${base}/transfer-owner`, { user_id: transferTo.value }), t('guildPages.members.transferred', { name: humanOptions.value.find((option) => option.value === transferTo.value).label }))
const leave = () => run(async () => {
  await api('POST', `${base}/leave`, {})
  await router.push('/guilds/mine')
}, t('guildPages.members.left'))
const invite = () => run(() => api('POST', `${base}/invitations`, { ...(inviteWho.value.includes('@') ? { email: inviteWho.value.trim() } : { handle: inviteWho.value.trim() }), role: inviteRole.value }), () => {
  const name = inviteWho.value.trim()
  inviteWho.value = ''
  return t('guildPages.members.inviteSent', { name })
})
const revokeInvite = (row) => run(() => api('DELETE', `${base}/${row.kind === 'agent' ? 'agent-invitations' : 'invitations'}/${encodeURIComponent(row.request_id)}`), t('guildPages.members.revoked', { name: row.name }))
const inviteAgent = () => run(async () => {
  await api('POST', `${base}/agent-invitations`, { agent_id: agentPick.value })
  const name = agentHitOptions.value.find((option) => option.value === agentPick.value).label
  agentQuery.value = ''
  return name
}, (name) => t('guildPages.members.agentInviteSent', { name }))
const add = () => run(async () => {
  await api('POST', `/api/voko/agents/${encodeURIComponent(addAgent.value)}/guilds`, { guild_slug: route.params.slug })
  addAgent.value = ''
}, t('guildPages.members.added'))
const logText = (entry) => {
  const params = { actor: entry.actor_handle, subject: entry.subject_handle ?? entry.subject_agent_name, from: entry.detail.from === undefined ? '' : t(`guildPages.members.humanRoles.${entry.detail.from}`), to: entry.detail.to === undefined ? '' : t(`guildPages.members.humanRoles.${entry.detail.to}`) }
  switch (entry.action) {
    case 'role_changed': return t('guildPages.members.logRoleChanged', params)
    case 'member_kicked': return t('guildPages.members.logKicked', params)
    case 'owner_transferred': return t('guildPages.members.logTransferred', params)
    case 'member_left': return t('guildPages.members.logLeft', params)
    case 'invited': return t('guildPages.members.logInvited', params)
    case 'invitation_revoked': return t('guildPages.members.logInvitationRevoked', params)
    case 'invitation_accepted': return t('guildPages.members.logInvitationAccepted', params)
    case 'invitation_declined': return t('guildPages.members.logInvitationDeclined', params)
    case 'agent_invited': return t('guildPages.members.logAgentInvited', params)
    case 'agent_invitation_revoked': return t('guildPages.members.logAgentInvitationRevoked', params)
    case 'agent_invitation_accepted': return t('guildPages.members.logAgentInvitationAccepted', params)
    case 'agent_invitation_declined': return t('guildPages.members.logAgentInvitationDeclined', params)
    default: throw new Error(`Unhandled action: ${entry.action}, id: ${entry.id}`)
  }
}
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.guildMembers') }}</p>
      <h1 class="page-head__title">{{ t('guildPages.members.title') }}{{ guild === null ? '' : ` · ${guild.name}` }}</h1>
    </div>
  </header>
  <p v-if="message" role="status">{{ message }}</p>
  <p v-if="error" role="alert">{{ error }}</p>
  <p v-if="guild !== null && guild.status === 'archived'">{{ t('guilds.manageForm.archivedNote') }}</p>
  <section v-else-if="guild !== null" class="voko-panel" :aria-label="t('guildPages.members.title')">
    <div class="gm-filters">
      <Button size="sm" :variant="tab === 'members' ? 'primary' : 'ghost'" @click="tab = 'members'">{{ t('guildPages.members.tabMembers') }}</Button>
      <Button v-if="isManager" size="sm" :variant="tab === 'log' ? 'primary' : 'ghost'" @click="tab = 'log'">{{ t('guildPages.members.tabLog') }}</Button>
      <Badge size="sm" variant="neutral">{{ myRole === 'platform' ? t('guildPages.members.platform') : t(`guildPages.members.humanRoles.${myRole}`) }}</Badge>
    </div>
    <template v-if="tab === 'members'">
      <p class="voko-muted">{{ t('guildPages.members.intro') }}</p>
      <div class="gm-filters">
        <Input v-model="query.q" type="search" :placeholder="t('guildPages.members.search')" :aria-label="t('guildPages.members.search')" />
        <Select v-model="query.type" :options="typeOptions" :aria-label="t('guildPages.members.colType')" size="sm" />
        <Select v-model="query.role" :options="roleOptions" :aria-label="t('guildPages.members.colRole')" size="sm" />
        <Select v-model="query.state" :options="stateOptions" :aria-label="t('guildPages.members.colState')" size="sm" />
      </div>
      <Table :columns="columns" :rows="result.rows.map((row) => ({ ...row, key: `${row.kind}:${row.id}:${row.state}` }))" :sort-key="query.sort" :sort-order="query.dir" @sort="sortBy">
        <template #name="{ row }"><strong>{{ row.name }}</strong><template v-if="row.github_login !== null"> · GitHub {{ row.github_login }}</template></template>
        <template #kind="{ row }">{{ t(`guildPages.members.${row.kind}`) }}</template>
        <template #owner_handle="{ row }">{{ row.owner_handle === null ? '' : `@${row.owner_handle}` }}</template>
        <template #role="{ row }">
          <Select v-if="row.kind === 'human' && isOwner && row.role !== 'owner' && row.state === 'active'" :model-value="row.role" :options="[{ value: 'admin', label: t('guildPages.members.humanRoles.admin') }, { value: 'member', label: t('guildPages.members.humanRoles.member') }]" :aria-label="t('guildPages.members.changeRole')" size="sm" :disabled="busy" @update:model-value="(role) => changeRole(row, role)" />
          <Badge v-else :variant="row.role === 'company' || row.role === 'owner' ? 'info' : 'neutral'" size="sm">{{ roleLabel(row) }}</Badge>
        </template>
        <template #state="{ row }"><Badge :variant="row.state === 'pending' ? 'warning' : 'success'" size="sm">{{ t(`guildPages.members.${row.state}`) }}</Badge></template>
        <template #joined_at="{ row }">{{ date(row.joined_at) }}</template>
        <template #level="{ row }">{{ row.level === null ? '' : row.level }}</template>
        <template #actions="{ row }">
          <template v-if="row.state === 'invited'">
            <ConfirmAction :label="t('guildPages.members.revoke')" :action="() => revokeInvite(row)" :confirm-text="t('guildPages.members.revokeConfirm', { name: row.name })" :cancel-label="t('guildPages.common.cancel')" :ok-label="t('guildPages.members.revoke')" :pending-label="t('guilds.manageForm.working')" danger />
          </template>
          <template v-else-if="row.state === 'pending'">
            <template v-if="isManager">
              <Button size="sm" :disabled="busy" @click="decide(row, 'approve')">{{ t('guilds.manageForm.approve') }}</Button>
              <Button size="sm" variant="ghost" :disabled="busy" @click="decide(row, 'reject')">{{ t('guilds.manageForm.reject') }}</Button>
            </template>
          </template>
          <template v-else>
            <Button v-if="canMakeCompany(row)" size="sm" variant="secondary" :disabled="busy" @click="makeCompany(row)">{{ t('guildPages.members.makeCompany') }}</Button>
            <ConfirmAction v-if="canRemoveAgent(row)" :label="t('guildPages.members.remove')" :action="() => removeAgent(row)" :confirm-text="t('guildPages.members.removeConfirm', { name: row.name })" :cancel-label="t('guildPages.common.cancel')" :ok-label="t('guildPages.members.remove')" :pending-label="t('guilds.manageForm.working')" danger />
            <ConfirmAction v-if="canKick(row)" :label="t('guildPages.members.kick')" :action="() => kick(row)" :confirm-text="t('guildPages.members.kickConfirm', { name: row.name })" :cancel-label="t('guildPages.common.cancel')" :ok-label="t('guildPages.members.kick')" :pending-label="t('guilds.manageForm.working')" danger />
          </template>
        </template>
      </Table>
      <p v-if="result.rows.length === 0" class="voko-muted">{{ t('guildPages.members.empty') }}</p>
      <div class="gm-filters">
        <Button size="sm" variant="ghost" :disabled="query.page <= 1" @click="query.page -= 1">{{ t('guildPages.members.prev') }}</Button>
        <span class="voko-muted">{{ t('guildPages.members.page', { page: result.page, pages, total: result.total }) }}</span>
        <Button size="sm" variant="ghost" :disabled="query.page >= pages" @click="query.page += 1">{{ t('guildPages.members.next') }}</Button>
      </div>
      <form v-if="isManager" class="gm-add" @submit.prevent="invite">
        <h2>{{ t('guildPages.members.inviteTitle') }}</h2>
        <p class="voko-muted">{{ t('guildPages.members.inviteIntro') }}</p>
        <div class="gm-filters">
          <Input v-model="inviteWho" :placeholder="t('guildPages.members.invitePlaceholder')" :aria-label="t('guildPages.members.invitePlaceholder')" />
          <Select v-model="inviteRole" :options="inviteRoleOptions" :aria-label="t('guildPages.members.colRole')" size="sm" />
          <Button type="submit" variant="secondary" :disabled="busy || inviteWho.trim() === ''">{{ t('guildPages.members.inviteSend') }}</Button>
        </div>
      </form>
      <form v-if="isManager" class="gm-add" @submit.prevent="inviteAgent">
        <h2>{{ t('guildPages.members.agentInviteTitle') }}</h2>
        <p class="voko-muted">{{ t('guildPages.members.agentInviteIntro') }}</p>
        <div class="gm-filters">
          <Input v-model="agentQuery" type="search" :placeholder="t('guildPages.members.agentSearchPlaceholder')" :aria-label="t('guildPages.members.agentSearchPlaceholder')" />
          <Select v-if="agentHitOptions.length > 0" v-model="agentPick" :options="agentHitOptions" :placeholder="t('guildPages.members.agentPick')" :aria-label="t('guildPages.members.agentPick')" />
          <Button type="submit" variant="secondary" :disabled="busy || agentPick === ''">{{ t('guildPages.members.agentInviteSend') }}</Button>
        </div>
      </form>
      <form v-if="addOptions.length > 0" class="gm-add" @submit.prevent="add">
        <h2>{{ t('guildPages.members.addMine') }}</h2>
        <p class="voko-muted">{{ t('guildPages.members.addMineIntro') }}</p>
        <div class="gm-filters">
          <Select v-model="addAgent" :options="addOptions" :placeholder="t('guilds.manageForm.bank.pickAgent')" :aria-label="t('guildPages.members.addMine')" />
          <Button type="submit" variant="secondary" :disabled="busy || addAgent === ''">{{ t('guildPages.members.add') }}</Button>
        </div>
      </form>
      <form v-if="myRole === 'owner' && humanOptions.length > 0" class="gm-add" @submit.prevent="transfer">
        <h2>{{ t('guildPages.members.transfer') }}</h2>
        <div class="gm-filters">
          <Select v-model="transferTo" :options="humanOptions" :placeholder="t('guildPages.members.colName')" :aria-label="t('guildPages.members.transfer')" />
          <ConfirmAction v-if="transferTo !== ''" :label="t('guildPages.members.transfer')" :action="transfer" :confirm-text="t('guildPages.members.transferHint', { name: humanOptions.find((option) => option.value === transferTo)?.label ?? '' })" :cancel-label="t('guildPages.common.cancel')" :ok-label="t('guildPages.members.transfer')" :pending-label="t('guilds.manageForm.working')" danger />
        </div>
      </form>
      <div v-if="myRole !== 'owner' && myRole !== 'platform'" class="gm-add">
        <ConfirmAction :label="t('guildPages.members.leave')" :action="leave" :confirm-text="t('guildPages.members.leaveConfirm')" :cancel-label="t('guildPages.common.cancel')" :ok-label="t('guildPages.members.leave')" :pending-label="t('guilds.manageForm.working')" danger />
      </div>
    </template>
    <template v-else>
      <ul class="gm-log">
        <li v-for="entry in log.entries" :key="entry.id"><span class="voko-muted">{{ new Date(entry.created_at).toLocaleString(locale) }}</span> {{ logText(entry) }}</li>
      </ul>
      <p v-if="log.entries.length === 0" class="voko-muted">{{ t('guildPages.members.noLog') }}</p>
      <div class="gm-filters">
        <Button size="sm" variant="ghost" :disabled="logPage <= 1" @click="logPage -= 1">{{ t('guildPages.members.prev') }}</Button>
        <Button size="sm" variant="ghost" :disabled="!log.has_more" @click="logPage += 1">{{ t('guildPages.members.next') }}</Button>
      </div>
    </template>
  </section>
</template>

<style scoped>
.voko-panel { margin-bottom: var(--drama-space-lg); }
.gm-filters { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); margin: var(--drama-space-sm) 0; }
.gm-add { margin-top: var(--drama-space-lg); }
.gm-log { list-style: none; margin: 0; padding: 0; }
.gm-log li { padding: var(--drama-space-xs) 0; }
.gm-add h2 { margin: 0 0 var(--drama-space-xs); font-size: var(--drama-font-size-md); }
</style>
